// Общая базовая подложка карты — единый стиль для всех разделов с картой.
// Раньше жил inline в MapCanvas; вынесен, чтобы раздел «Спутники» (мировая
// карта на raw MapLibre) рисовал ту же подложку без копипасты.
//
// С 27.08.2026 подложка — не константа, а НАСТРОЙКА ДОМЕНА: Carto ввёл
// обязательный ключ, и стало видно, что чья это картография — вопрос домена
// (см. `basemap.ts`). Регистр модульный, как у цветов бренда
// (`applyBrandMapColors`): стиль строит императивный код при создании карты,
// контекста React там нет.

import type maplibregl from 'maplibre-gl'
import { CARTO_BASEMAP, type BasemapSpec } from './basemap'

/** Рекурсивно замораживает объект и всё вложенное (in place). */
function deepFreeze<T>(o: T): T {
  Object.freeze(o)
  for (const key of Object.keys(o as object)) {
    const v = (o as Record<string, unknown>)[key]
    if (v && (typeof v === 'object' || typeof v === 'function') && !Object.isFrozen(v)) {
      deepFreeze(v)
    }
  }
  return o
}

/** Идентификатор источника и слоя подложки. ПОСТОЯНЕН при любом поставщике:
 *  относительно него уложены все слои карты (`layerOrder`), и переименование
 *  вслед за фирмой разобрало бы стопку. Историческое имя `carto` — это имя
 *  МЕСТА в стопке, а не имя поставщика. */
export const BASE_SOURCE_ID = 'carto'

let current: BasemapSpec = CARTO_BASEMAP
let cached: maplibregl.StyleSpecification | null = null

/** Подложка домена. Зовётся ОДИН раз, до первого кадра (`LangProvider`):
 *  `new maplibregl.Map({style: baseStyle()})` синхронен, и подложка, приехавшая
 *  позже, дала бы карту, моргающую чужой картографией. */
export function applyBasemap(spec: BasemapSpec): void {
  current = spec
  cached = null
}

/** Текущая подложка. Спрашивают подпись (её требуют условия обоих поставщиков)
 *  и полоска отказа: пустая подложка обязана называть себя. */
export function activeBasemap(): BasemapSpec {
  return current
}

/**
 * Стиль подложки.
 *
 * Заморожен целиком (объект, sources, массив/объекты layers): подложку по
 * ссылке делят разделы с картой, и мутация из одного незаметно поехала бы в
 * другом. MapLibre держит стиль по ссылке, но не мутирует его (клонирует
 * внутри), поэтому freeze безопасен и делает гарантию структурной.
 *
 * ИДЕНТИФИКАТОРЫ ИСТОЧНИКА И СЛОЯ ПОСТОЯННЫ (`carto`) при любом поставщике:
 * относительно них уложены ВСЕ слои карты (`layerOrder`), и переименование
 * вслед за фирмой разобрало бы стопку. Это имя места в стопке, а не имя
 * поставщика.
 */
export function baseStyle(): maplibregl.StyleSpecification {
  if (!cached) {
    const source: Record<string, unknown> = {
      type: 'raster',
      tiles: [...current.tiles],
      tileSize: current.tileSize,
    }
    // Ключ добавляем ТОЛЬКО когда потолок задан: у Carto его нет с самого
    // начала, и `maxzoom: undefined` в стиле — это уже не «как было».
    if (current.maxzoom !== undefined) source.maxzoom = current.maxzoom
    cached = deepFreeze<maplibregl.StyleSpecification>({
      version: 8,
      sources: { [BASE_SOURCE_ID]: source as never },
      layers: [
        { id: BASE_SOURCE_ID, type: 'raster', source: BASE_SOURCE_ID },
      ],
    })
  }
  return cached
}
